<template>
  <Teleport to="body">
    <Transition name="toast-error">
      <div
        v-if="modelValue"
        class="toast-error"
        role="alert"
        aria-live="assertive"
      >
        <div class="toast-error-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <p class="toast-error-message">{{ message }}</p>
        <button
          v-if="showClose"
          type="button"
          class="toast-error-close"
          aria-label="Close"
          @click="close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
interface Props {
  /** Текст помилки */
  message?: string
  /** Показувати кнопку закриття */
  showClose?: boolean
  /** Автоматично закривати через N мілісекунд (0 = не закривати) */
  autoClose?: number
}

const props = withDefaults(defineProps<Props>(), {
  message: undefined,
  showClose: true,
  autoClose: 5000,
})

const modelValue = defineModel<boolean>({ default: false })

let autoCloseTimer: ReturnType<typeof setTimeout> | null = null

function close() {
  modelValue.value = false
}

watch(() => modelValue.value, (isOpen) => {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer)
    autoCloseTimer = null
  }
  
  if (isOpen && props.autoClose > 0) {
    autoCloseTimer = setTimeout(() => {
      close()
    }, props.autoClose)
  }
})

onUnmounted(() => {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer)
  }
})
</script>

<style scoped>
.toast-error {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  min-width: 300px;
  max-width: 500px;
  background: #FFFFFF;
  border: 1px solid #FED7D7;
  border-left: 4px solid var(--color-error);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-family: var(--font-inter);
}

.toast-error-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: var(--color-error);
}

.toast-error-message {
  flex: 1;
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-dark-blue);
}

.toast-error-close {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-grey);
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s, color 0.2s;
}

.toast-error-close:hover {
  background: var(--color-light-grey);
  color: var(--color-dark-blue);
}

.toast-error-enter-active,
.toast-error-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.toast-error-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-error-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.toast-error-enter-to,
.toast-error-leave-from {
  opacity: 1;
  transform: translateX(0);
}

@media (max-width: 575px) {
  .toast-error {
    top: 16px;
    right: 16px;
    left: 16px;
    min-width: auto;
    max-width: none;
  }
}
</style>
